import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { CompanyVoteProposalNameFormComponent } from '../company-vote-proposal-name-form/company-vote-proposal-name-form.component';
import { CompanyVoteProposalNameService } from '../../../services/company-vote-proposal-name.service';
import { CompanyVoteProposalNameDto } from '../../../models/api/companyVoteProposalNameDto';
import { UpdateCompanyVoteProposalNameRequest } from '../../../models/api/updateCompanyVoteProposalNameRequest';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { ApiErrorResponse } from '../../../models/api/apiErrorResponse';
import { ErrorCodes } from '../../../models/api/errorCodes';
import { MaterialModule } from '../../../shared/material.module';

@Component({
  selector: 'app-company-vote-proposal-name-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CompanyVoteProposalNameFormComponent,
    NgxSpinnerModule,
    MaterialModule
  ],
  templateUrl: './company-vote-proposal-name-edit.component.html',
  styleUrls: ['./company-vote-proposal-name-edit.component.scss']
})
export class CompanyVoteProposalNameEditComponent implements OnInit, OnDestroy {
  form: FormGroup;
  proposalNameId: string | null = null;
  proposalNameData: CompanyVoteProposalNameDto | null = null;
  showYearWarning: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private proposalNameService: CompanyVoteProposalNameService,
    private router: Router,
    private route: ActivatedRoute,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {
    this.form = this.fb.group({
      year: ['', Validators.required],
      fullName: ['', Validators.required],
      address: [''],
      website: [''],
      email: [''],
      country: [''],
      category: [''],
      subcategory: [''],
      notes: ['']
    });
  }

  ngOnInit() {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const id = params.get('id');
        this.proposalNameId = id;

        if (this.proposalNameId) {
          const year = this.route.snapshot.queryParamMap.get('year');
          this.loadProposalNameData(year ? parseInt(year) : undefined);
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadProposalNameData(year?: number, skipYearPatch: boolean = false) {
    if (!this.proposalNameId) return;

    this.spinnerService.show();
    this.proposalNameService.getById(
      this.proposalNameId,
      year
    ).subscribe({
      next: (data) => {
        this.proposalNameData = data;
        this.showYearWarning = false;

        const patchData: any = {
          fullName: data.fullName,
          address: data.address,
          website: data.website,
          email: data.email,
          country: data.countryId ? { id: data.countryId, name: data.countryName } : null,
          notes: data.notes
        };

        if (!skipYearPatch) {
          patchData.year = year?.toString();
        }

        this.form.patchValue(patchData, { emitEvent: false });

        if (data.categoryId) {
          this.form.patchValue({
            category: { id: data.categoryId, name: data.categoryName }
          }, { emitEvent: false });
        } else {
          this.form.patchValue({
            category: null
          }, { emitEvent: false });
        }

        setTimeout(() => {
          if (data.subCategoryId) {
            this.form.patchValue({
              subcategory: { id: data.subCategoryId, name: data.subCategoryName }
            }, { emitEvent: false });
          } else {
            this.form.patchValue({
              subcategory: null
            }, { emitEvent: false });
          }
        }, 100);

        this.spinnerService.hide();
      },
      error: (error: any) => {
        this.spinnerService.hide();

        const apiError = error.error as ApiErrorResponse;

        if (apiError.errorCode === ErrorCodes.NFO_YEAR_NO_RECORD) {
          const currentYear = this.form.get('year')?.value;
          this.form.reset();
          this.form.patchValue({ year: currentYear }, { emitEvent: false });
          this.showYearWarning = true;
        } else if (apiError.errorCode === ErrorCodes.NFO_ENTITY_NOT_FOUND) {
          // Entity not found
        } else {
          this.toast.error('Error loading shareholder proposal name', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
        }

        console.error('Error loading shareholder proposal name:', error);
      }
    });
  }

  onYearChange(year: string) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { year: year },
      queryParamsHandling: 'merge'
    });

    this.loadProposalNameData(parseInt(year), true);
  }

  onSubmit() {
    if (this.form.valid && this.proposalNameId) {
      this.spinnerService.show();

      const formValue = this.form.value;
      const request: UpdateCompanyVoteProposalNameRequest = {
        year: parseInt(formValue.year),
        fullName: formValue.fullName,
        address: formValue.address || '',
        website: formValue.website || '',
        email: formValue.email || '',
        countryId: formValue.country?.id || formValue.country,
        categoryId: formValue.category?.id || formValue.category,
        subCategoryId: formValue.subcategory?.id || formValue.subcategory || '',
        notes: formValue.notes || ''
      };

      this.proposalNameService.update(this.proposalNameData?.id!, request).subscribe({
        next: () => {
          this.spinnerService.hide();
          this.toast.success('Shareholder proposal name updated successfully', {
            iconTheme: {
              primary: 'var(--mat-sys-primary)',
              secondary: '#FFF',
            }
          });
          this.router.navigate(['/voting-results/shareholder-proposal-names/list']);
        },
        error: (error: any) => {
          this.spinnerService.hide();
          this.toast.error('Error updating shareholder proposal name', {
            iconTheme: {
              primary: 'var(--mat-sys-error)',
              secondary: '#FFF',
            }
          });
          console.error('Error updating shareholder proposal name:', error);
        }
      });
    }
  }

  onCancel() {
    this.router.navigate(['/voting-results/shareholder-proposal-names/list']);
  }
}
